import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';

import StyledDataGrid from '@/components/datagrid/StyledDataGrid';
import DataGridNoRowsCustomOverlay from '@/components/datagrid/DataGridNoRowsOverlay';
import SensorDataGridToolbar from './SensorDataGridToolbar';
import SensorDetailDrawer from './SensorDetailDrawer';

const SensorDataGrid = (props) => {
  const {
    sensors,
    isLoading,
    unitData,
    setSelectedSensor,
    renderChart,
    handlePatchSensor,
    handleSupportClick,
  } = props;
  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    const initialRowsData = sensors?.map((sensor) => {
      const dateNow = new Date();
      const dataLastRecorded = new Date(sensor.DataLastRecorded);

      // Pre-calculate "Online" or "Offline" and add it to the rows
      const status =
        (dateNow - dataLastRecorded) / 1000 < 3600 ? 'Online' : 'Offline';

      return {
        id: sensor.SensorId,
        SensorId: sensor.SensorId,
        Online: status, // Add the derived status here
        DataLastRecorded: sensor.DataLastRecorded,
        IsConfigured: sensor.IsConfigured,
        SensorType: sensor.SensorType,
        ChannelCount: sensor.ChannelCount,
        DataTypes: sensor.DataTypes.join(', '),
        AccountName: sensor.Account.Name,
        DataInterval: sensor.DataInterval,
        TargetDataInterval: sensor.TargetDataInterval,
        CurrentApplicationFirmware: sensor.CurrentApplicationFirmware,
        CurrentCommonFirmware: sensor?.CurrentCommonFirmware,
        CalibrationType: sensor.CalibrationType,
        LatestActivity: sensor.LatestSensorActivities.map(
          (act) =>
            `${act.LastActivity} (RSSI: ${act.ReceivedSignalStrengthIndication}, LQI: ${act.LinkQualityIndication})`,
        ).join('; '),
        sensorObject: sensor,
      };
    });
    setRows(initialRowsData);
  }, [sensors]);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows?.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleRowClick = (params) => {
    setSelectedRow(params.row);
    setSelectedSensor(params.row);
    setDrawerOpen(true);
  };

  const getRowClassName = (params) => {
    return selectedRow && selectedRow.id === params.id ? 'selected-row' : '';
  };

  const columns = [
    {
      field: 'Online',
      headerName: 'Online',
      width: 75,
      renderCell: (params) => {
        const backGroundColor =
          params.value === 'Online'
            ? 'success.main'
            : params.value === 'Offline'
              ? 'error.main'
              : 'warning.main';

        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: backGroundColor,
              }}
            />
          </Box>
        );
      },
      filterable: true, // Ensure the quick filter works for this column
    },
    { field: 'id', headerName: 'Sensor ID', flex: 1 },
    { field: 'DataLastRecorded', headerName: 'Last Recorded', flex: 1 },
    {
      field: 'IsConfigured',
      headerName: 'Configured',
      type: 'boolean',
      flex: 1,
    },
    { field: 'SensorType', headerName: 'Type', flex: 1 },
    { field: 'ChannelCount', headerName: 'Channels', flex: 1 },
    { field: 'DataTypes', headerName: 'Data Types', flex: 1 },
    { field: 'LatestActivity', headerName: 'Latest Activity', flex: 1 },
    { field: 'AccountName', headerName: 'Account', flex: 1 },
    { field: 'DataInterval', headerName: 'Data Interval', flex: 1 },
    {
      field: 'TargetDataInterval',
      headerName: 'Target Data Interval',
      flex: 1,
    },
    { field: 'CurrentApplicationFirmware', headerName: 'Firmware', flex: 1 },
    { field: 'CurrentCommonFirmware', headerName: 'Common Firmware', flex: 1 },
    { field: 'CalibrationType', headerName: 'Calibration Type', flex: 1 },
  ];

  return (
    <>
      <StyledDataGrid
        rows={rows}
        columns={columns}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onRowClick={handleRowClick}
        getRowClassName={getRowClassName}
        noRowsOverlay={DataGridNoRowsCustomOverlay}
        toolbar={SensorDataGridToolbar}
        isLoading={isLoading}
        height="92vh"
        rowsName="Sensors"
      />
      <SensorDetailDrawer
        sensor={selectedRow}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        unitData={unitData}
        renderChart={renderChart}
        handlePatchSensor={handlePatchSensor}
        isLoading={isLoading}
        handleSupportClick={handleSupportClick}
      />
    </>
  );
};

export default SensorDataGrid;
